import {
  completeProfileFn,
  updateUserInfoFn,
} from "@/api/auth/auth.query.options";
import { mintGuestAndAttachCustomer } from "@/api/auth/guest.utils";
import { setToken } from "@/api/auth/token";
import { merchantQueryOptions } from "@/api/vendure/merchant/merchant.query.options";
import type { AttachCustomerPayload } from "@/api/vendure/order/order.types";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  completeProfileOnConfirmAtom,
  completeProfileSheetOpenAtom,
  currentUserAtom,
  guestDetailsAtom,
  isAuthAtom,
  phoneVerifiedTokenAtom,
} from "@/stores/auth.store";
import { orderAtom } from "@/stores/cart.store";
import { deliveryMethodAtom } from "@/stores/delivery.store";
import { currentVenueSlugAtom } from "@/stores/venue.store";
import { useForm } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { X } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { isValidPhoneNumber, PhoneInputField } from "./PhoneInputField";

const ERROR_CODE_MESSAGES: Record<string, string> = {
  EMAIL_ADDRESS_CONFLICT_ERROR: "errors.emailConflict",
};

export function CompleteProfileSheet() {
  const { t } = useTranslation();
  const [open, setOpen] = useAtom(completeProfileSheetOpenAtom);
  const [onConfirm, setOnConfirm] = useAtom(completeProfileOnConfirmAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const guestDetails = useAtomValue(guestDetailsAtom);
  const setGuestDetails = useSetAtom(guestDetailsAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const setOrder = useSetAtom(orderAtom);
  const setIsAuth = useSetAtom(isAuthAtom);
  const [phoneVerifiedToken, setPhoneVerifiedToken] = useAtom(phoneVerifiedTokenAtom);
  const deliveryMethod = useAtomValue(deliveryMethodAtom);
  const queryClient = useQueryClient();
  const slug = useAtomValue(currentVenueSlugAtom);
  const { data: venues } = useQuery(merchantQueryOptions(slug));
  const venue = venues?.[0];
  const channelToken = venue?.token ?? "";
  const isNewPhoneUser = !currentUser && !!phoneVerifiedToken;
  const isGuest = !currentUser && !phoneVerifiedToken;
  const isDineIn = deliveryMethod === "dine_in";
  const showEmail = !isDineIn;
  const showPhone =
    isGuest && (!isDineIn || venue?.guest_info_level === "name_and_phone");

  function freshValues() {
    return {
      firstName: currentUser?.firstName ?? guestDetails?.firstName ?? "",
      lastName: currentUser?.lastName ?? guestDetails?.lastName ?? "",
      email: currentUser?.emailAddress ?? guestDetails?.email ?? "",
      countryCode:
        currentUser?.phoneCountryCode ?? guestDetails?.countryCode ?? "+358",
      phoneNumber: currentUser?.phoneNumber ?? guestDetails?.phoneNumber ?? "",
    };
  }

  const form = useForm({
    defaultValues: freshValues(),
    onSubmit: async ({ value }) => {
      if (isNewPhoneUser) {
        try {
          const res = await completeProfileFn({
            data: {
              phoneVerifiedToken: phoneVerifiedToken!,
              firstName: value.firstName.trim(),
              lastName: value.lastName.trim(),
              emailAddress: value.email.trim(),
            },
          });
          setToken(res.authHeaderValue);
          setIsAuth(true);
          setCurrentUser(res.customer);
          setPhoneVerifiedToken(null);
          toast.success(t("common.status.success"));
          setOpen(false);
          onConfirm?.();
          setOnConfirm(null);
        } catch (err) {
          let message = t("errors.generic");
          if (err instanceof Error) {
            try {
              const parsed = JSON.parse(err.message) as { message?: string };
              if (parsed.message) message = parsed.message;
            } catch {}
          }
          toast.error(message);
        }
      } else if (isGuest) {
        const guestValue: AttachCustomerPayload = {
          firstName: value.firstName,
          lastName: value.lastName,
          countryCode: value.countryCode,
          emailAddress: showEmail ? value.email : undefined,
          phoneNumber: showPhone ? value.phoneNumber || undefined : undefined,
        };

        try {
          const data = await mintGuestAndAttachCustomer(channelToken, guestValue);
          setOrder(data.cart);
          setIsAuth(true);
          setGuestDetails({
            firstName: value.firstName,
            lastName: value.lastName,
            email: showEmail ? value.email : "",
            countryCode: value.countryCode,
            phoneNumber: showPhone ? value.phoneNumber : "",
          });
          toast.success(t("common.status.success"));
          setOpen(false);
          onConfirm?.();
          setOnConfirm(null);
        } catch (err) {
          const data = axios.isAxiosError(err) ? err.response?.data : undefined;
          const messageKey = data?.errorCode
            ? ERROR_CODE_MESSAGES[data.errorCode]
            : undefined;
          toast.error(
            messageKey ? t(messageKey) : (data?.message ?? t("errors.generic")),
          );
        }
      } else {
        await toast.promise(
          updateUserInfoFn(
            value.firstName.trim(),
            value.lastName.trim(),
            value.email.trim() || undefined,
          ).then((updated) => {
            setCurrentUser(updated);
            queryClient.invalidateQueries({ queryKey: ["userinfo"] });
            setOpen(false);
            onConfirm?.();
            setOnConfirm(null);
          }),
          {
            loading: t("common.status.loading"),
            success: t("common.status.updated"),
            error: t("errors.generic"),
          },
        );
      }
    },
  });

  useEffect(() => {
    if (open) form.reset(freshValues());
  }, [open]);

  return (
    <Drawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !isNewPhoneUser) setOpen(false);
      }}
      repositionInputs={false}
    >
      <DrawerContent className="rounded-t-3xl md:inset-x-[25%]!">
        <div className="px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-6">
            <DrawerTitle className="text-xl font-bold text-foreground">
              {t("business.auth.detailsTitle")}
            </DrawerTitle>
            {!isNewPhoneUser && (
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full bg-muted flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <DrawerDescription className="text-base font-semibold text-foreground mb-5">
            {t("business.auth.yourDetails")}
          </DrawerDescription>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex flex-col gap-3"
          >
            <div className="flex gap-3 w-full">
              <form.Field name="firstName">
                {(field) => (
                  <input
                    data-testid="complete-profile.firstName"
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={`${t("business.profile.firstName")}*`}
                    className="min-w-0 flex-1 bg-muted rounded-2xl px-4 py-4 text-base text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary"
                  />
                )}
              </form.Field>
              <form.Field name="lastName">
                {(field) => (
                  <input
                    data-testid="complete-profile.lastName"
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={`${t("business.profile.lastName")}*`}
                    className="min-w-0 flex-1 bg-muted rounded-2xl px-4 py-4 text-base text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary"
                  />
                )}
              </form.Field>
            </div>

            {showEmail && (
              <form.Field name="email">
                {(field) => (
                  <div className="relative">
                    <input
                      data-testid="complete-profile.email"
                      id={field.name}
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder={`${t("business.auth.emailAddress")}*`}
                      className="w-full bg-muted rounded-2xl px-4 py-4 pr-24 text-base text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold pointer-events-none">
                      {t("business.auth.receipt")}
                    </span>
                  </div>
                )}
              </form.Field>
            )}

            {showPhone && (
              <form.Subscribe
                selector={(s) => ({
                  cc: s.values.countryCode,
                  ph: s.values.phoneNumber,
                })}
              >
                {({ cc, ph }) => (
                  <PhoneInputField
                    countryCode={cc}
                    phoneNumber={ph}
                    onCountryCodeChange={(v) =>
                      form.setFieldValue("countryCode", v)
                    }
                    onPhoneNumberChange={(v) =>
                      form.setFieldValue("phoneNumber", v)
                    }
                    placeholder={`${t("business.auth.phoneNumber")}*`}
                  />
                )}
              </form.Subscribe>
            )}

            <form.Subscribe
              selector={(s) => ({
                isSubmitting: s.isSubmitting,
                values: s.values,
              })}
            >
              {({ isSubmitting, values }) => (
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !values.firstName.trim() ||
                    !values.lastName.trim() ||
                    (showEmail && !values.email.trim()) ||
                    (showPhone &&
                      !isValidPhoneNumber(
                        values.phoneNumber.replace(/\D/g, ""),
                      ))
                  }
                  data-testid="complete-profile.confirm"
                  className="w-full rounded-full h-13 text-base font-semibold mt-2"
                >
                  {t("common.actions.confirm")}
                </Button>
              )}
            </form.Subscribe>
          </form>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
